

















































































































































































import { Component, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { getModule } from "vuex-module-decorators";

import FormField from "@/lib/components/FormField.vue";
import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import dataStore from "@/store";

import DistributionCenter from "../distribution-center.model";
import ShippingManifest from "../shipping-manifest.model";
import buildCorrectionRequest from "../build-correction-request.function";
import hasCorrectionBoxChanged from "../has-correction-box-changed.function";
import isCorrectionBoxComplete from "../is-correction-box-complete.function";
import {
  ShippingManifestDestinationCorrectionBoxValue,
  ShippingManifestDestinationCorrectionShipmentValue,
} from "../shipping-manifest-destination-correction-form.value";
import BulkShipmentStore from "../store";

interface DimensionField {
  field: "length" | "width" | "height" | "weight";
  label: string;
  min: number;
  step: number;
  validation: string;
}

@Component({
  components: {
    FormErrors,
    FormField,
    LoadingSpinner,
    SubmitButton,
  },
})
export default class ShippingManifestDestinationCorrectionForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly shippingManifestId!: string;

  @Prop({ default: "" })
  public readonly currentDistributionCenterId!: string;

  @Prop({ required: true })
  public readonly distributionCenters!: DistributionCenter[];

  public distributionCenter = "";
  public boxDrafts: Record<
    string,
    ShippingManifestDestinationCorrectionBoxValue
  > = {};

  readonly dimensions: DimensionField[] = [
    {
      field: "length",
      label: "Length, cm",
      min: 1,
      step: 1,
      validation: "required|integer|min_value:1",
    },
    {
      field: "width",
      label: "Width, cm",
      min: 1,
      step: 1,
      validation: "required|integer|min_value:1",
    },
    {
      field: "height",
      label: "Height, cm",
      min: 1,
      step: 1,
      validation: "required|integer|min_value:1",
    },
    {
      field: "weight",
      label: "Weight, kg",
      min: 0,
      step: 0.01,
      validation: "required|decimal|min_value:0",
    },
  ];

  private fManifest: ShippingManifest | null = null;
  private fShipments: ShippingManifestDestinationCorrectionShipmentValue[] = [];
  private fIsLoading = true;
  private fBulkShipmentStore: BulkShipmentStore;

  constructor() {
    super();
    this.fBulkShipmentStore = getModule(BulkShipmentStore, dataStore);
  }

  get shipmentCount(): number {
    return this.shipments.length;
  }

  get manifest(): ShippingManifest | null {
    return this.fManifest;
  }

  get shipments(): ShippingManifestDestinationCorrectionShipmentValue[] {
    return this.fShipments;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get activeDistributionCenters(): DistributionCenter[] {
    return this.distributionCenters
      .filter((center) => center.isActive)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get currentDestinationName(): string {
    const center = this.distributionCenters.find(
      (item) => item.id === this.currentDistributionCenterId
    );

    return center?.name || "";
  }

  get selectedDestination(): DistributionCenter | undefined {
    return this.activeDistributionCenters.find(
      (center) => center.id === this.distributionCenter
    );
  }

  get isBoxParametersRequired(): boolean {
    if (!this.selectedDestination) {
      return false;
    }

    const id = this.selectedDestination.id.toUpperCase();
    const code = this.selectedDestination.code.toUpperCase();

    return (
      id === "PORTLESS" ||
      code === "PORTLESS" ||
      this.selectedDestination.isBoxParametersRequired
    );
  }

  get isDirty(): boolean {
    return (
      this.distributionCenter !== this.currentDistributionCenterId ||
      (this.isBoxParametersRequired &&
        this.shipments.some((shipment) => this.hasBoxChanged(shipment)))
    );
  }

  get isDisabled(): boolean {
    return (
      this.fIsDisabled ||
      this.disable ||
      !this.isDirty ||
      !this.areRequiredBoxesValid
    );
  }

  get areRequiredBoxesValid(): boolean {
    return (
      !this.isBoxParametersRequired ||
      this.shipments.every((shipment) =>
        this.isEffectiveBoxComplete(shipment.id)
      )
    );
  }

  public shipmentRoute(shipmentId: string): Record<string, unknown> {
    return {
      name: "BulkShipmentViewPage",
      query: { id: shipmentId },
    };
  }

  public boxFieldName(shipmentId: string, field: string): string {
    return `box-${shipmentId}-${field}`;
  }

  public boxErrors(shipmentId: string): string[] {
    return ["boxNumber", "length", "width", "height", "weight"]
      .map((field) => this.boxFieldError(shipmentId, field))
      .filter((error): error is string => !!error);
  }

  public boxFieldError(shipmentId: string, field: string): string | undefined {
    if (field === "boxNumber" && this.hasDuplicateBoxNumber(shipmentId)) {
      return "Box number must be unique within this manifest.";
    }

    const error = this.$validator.errors.first(
      this.boxFieldName(shipmentId, field)
    );

    return error || undefined;
  }

  public isEffectiveBoxComplete(shipmentId: string): boolean {
    return (
      isCorrectionBoxComplete(this.boxDrafts[shipmentId]) &&
      !this.hasDuplicateBoxNumber(shipmentId)
    );
  }

  public onCancelClick(): void {
    this.$emit("cancel");
  }

  protected created(): void {
    void this.loadData();
  }

  protected async performSubmit(): Promise<void> {
    if (
      !this.manifest ||
      !this.distributionCenter ||
      !this.isDirty ||
      !this.areRequiredBoxesValid
    ) {
      return;
    }

    const request = buildCorrectionRequest(
      this.manifest.id,
      this.distributionCenter,
      this.shipments,
      this.boxDrafts,
      this.isBoxParametersRequired
    );

    await this.fBulkShipmentStore.correctShippingManifestDestination(request);
    this.$emit("saved");
  }

  private hasDuplicateBoxNumber(shipmentId: string): boolean {
    const boxNumber = this.boxDrafts[shipmentId]?.boxNumber;

    if (boxNumber === undefined || boxNumber === null) {
      return false;
    }

    return this.shipments.some((shipment) => {
      return (
        shipment.id !== shipmentId &&
        this.boxDrafts[shipment.id]?.boxNumber === boxNumber
      );
    });
  }

  private hasBoxChanged(
    shipment: ShippingManifestDestinationCorrectionShipmentValue
  ): boolean {
    return hasCorrectionBoxChanged(shipment.box, this.boxDrafts[shipment.id]);
  }

  private copyBox(
    box?: ShippingManifestDestinationCorrectionBoxValue
  ): ShippingManifestDestinationCorrectionBoxValue {
    return box
      ? { ...box }
      : {
          boxNumber: undefined,
          length: undefined,
          width: undefined,
          height: undefined,
          weight: undefined,
        };
  }

  private async loadData(): Promise<void> {
    this.fIsLoading = true;
    this.submitErrors = [];

    try {
      const [manifest, relatedShipments] = await Promise.all([
        this.fBulkShipmentStore.loadShippingManifestById({
          id: this.shippingManifestId,
          useCache: false,
        }),
        this.fBulkShipmentStore.loadBulkShipmentsByShippingManifestId(
          this.shippingManifestId
        ),
      ]);

      if (!manifest) {
        throw new Error("Shipping manifest was not found.");
      }

      this.fManifest = manifest;
      this.fShipments = relatedShipments.map((shipment) => {
        const box = this.fBulkShipmentStore.getBulkShipmentBoxById(shipment.id);

        return {
          id: shipment.id,
          displayId: String(shipment.incrementalId || shipment.id),
          box: box
            ? {
                boxNumber: box.boxNumber,
                length: box.length,
                width: box.width,
                height: box.height,
                weight: box.weight,
              }
            : undefined,
        };
      });
    } catch (error) {
      this.submitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsLoading = false;
    }
  }

  @Watch("shipments", { immediate: true, deep: true })
  private initializeBoxDrafts(): void {
    const drafts: Record<
      string,
      ShippingManifestDestinationCorrectionBoxValue
    > = {};

    this.shipments.forEach((shipment) => {
      drafts[shipment.id] = this.copyBox(shipment.box);
    });

    this.boxDrafts = drafts;
  }

  @Watch("currentDistributionCenterId", { immediate: true })
  private initializeDestination(): void {
    this.distributionCenter = this.currentDistributionCenterId;
  }
}
